import { ExperienceResourceArray } from '../../../Types';
import { LearningDesign } from '../../../Types/SectionTemplates';

/**
 * Filter option data structure matching V1 pattern
 */
export interface CompetencyFilterOptionData {
  id: number;
  name: string;
  count: number;
}

export interface ProductTypeFilterOptionData {
  id: number;
  name: string;
  count: number;
}

/**
 * Product type labels matching V1's mapping
 */
const PRODUCT_TYPE_LABELS: Record<string, string> = {
  epc: 'Professor-Led',
  aep: 'On-Demand',
  coaching: 'Leadership Coaching',
  'group-coaching': 'Group Coaching',
  'program-sequence': 'Development Path',
};

/**
 * Get all experience IDs referenced by curated sections
 */
const getAllExperienceIds = (curatedSections: LearningDesign[]): number[] => {
  const allIds = new Set<number>();
  curatedSections.forEach((section) => {
    section.experiences.forEach((id) => allIds.add(id));
  });
  return Array.from(allIds);
};

/**
 * Derive all unique competency filter options from experiences in curated sections
 */
export const deriveCompetencyFilterOptions = (
  curatedSections: LearningDesign[],
  experiencesArray: ExperienceResourceArray,
): CompetencyFilterOptionData[] => {
  const relevantExpIds = new Set(getAllExperienceIds(curatedSections));

  // Count occurrences of each competency across relevant experiences
  const competencyCounts: Record<string, number> = {};

  experiencesArray
    .filter((exp) => relevantExpIds.has(exp.payload.id))
    .forEach((exp) => {
      exp.payload.competency_names.forEach((name) => {
        competencyCounts[name] = (competencyCounts[name] || 0) + 1;
      });
    });

  // Convert to filter option data format
  return Object.entries(competencyCounts)
    .map(([name, count], index) => ({
      id: index + 1,
      name,
      count,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Derive all unique product type filter options from experiences in curated sections
 */
export const deriveProductTypeFilterOptions = (
  curatedSections: LearningDesign[],
  experiencesArray: ExperienceResourceArray,
): ProductTypeFilterOptionData[] => {
  const relevantExpIds = new Set(getAllExperienceIds(curatedSections));

  // Count occurrences of each product type across relevant experiences
  const productTypeCounts: Record<string, number> = {};

  experiencesArray
    .filter((exp) => relevantExpIds.has(exp.payload.id))
    .forEach((exp) => {
      const offeringType = exp.payload.offering_type;
      productTypeCounts[offeringType] = (productTypeCounts[offeringType] || 0) + 1;
    });

  // Convert to filter option data format with friendly labels
  return Object.entries(productTypeCounts)
    .map(([name, count], index) => ({
      id: index + 1,
      name,
      count,
    }))
    .sort((a, b) => getProductTypeLabel(a.name).localeCompare(getProductTypeLabel(b.name)));
};

/**
 * Get friendly label for a product type
 */
export const getProductTypeLabel = (productType: string): string =>
  PRODUCT_TYPE_LABELS[productType] || productType;

/**
 * Derive competencies for a single curated section from its experiences
 */
export const deriveSectionCompetencies = (
  section: LearningDesign,
  experiencesArray: ExperienceResourceArray,
): string[] => {
  const competencies = new Set<string>();

  section.experiences.forEach((expId) => {
    const experience = experiencesArray.find((e) => e.payload.id === expId);
    if (experience) {
      experience.payload.competency_names.forEach((name) => competencies.add(name));
    }
  });

  return Array.from(competencies);
};

/**
 * Derive offering types for a single curated section from its experiences
 */
export const deriveSectionOfferingTypes = (
  section: LearningDesign,
  experiencesArray: ExperienceResourceArray,
): string[] => {
  const offeringTypes = new Set<string>();

  section.experiences.forEach((expId) => {
    const experience = experiencesArray.find((e) => e.payload.id === expId);
    if (experience) {
      offeringTypes.add(experience.payload.offering_type);
    }
  });

  return Array.from(offeringTypes);
};
